import { Address } from "../../model/Address";

/**
 * This function will take an autocomplete suggestion and, if possible,
 * return a compiled address.
 *
 * @param suggestion google.maps.places.AutocompleteSuggestion
 * @returns
 */
const getSuggestionAsAddress = async (
  suggestion: google.maps.places.AutocompleteSuggestion,
): Promise<Address | undefined> => {
  try {
    const result = await getSuggestionAsPlace(suggestion);
    if (result) return getPlaceAsAddress(result);
    else return undefined;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

/**
 * This will take an autocomplete suggestion and request the place
 * information from Google Places API.
 * @param suggestion The autocomplete suggestion
 * @returns Promise of place or undefined
 */
const getSuggestionAsPlace = async (
  suggestion: google.maps.places.AutocompleteSuggestion,
  fields?: Array<string>,
): Promise<google.maps.places.Place | undefined> => {
  const placeId = suggestion.placePrediction?.placeId;
  if (!placeId) return undefined;

  const place = new google.maps.places.Place({ id: placeId });

  const fieldsToGet = fields
    ? fields
    : ["displayName", "websiteURI", "nationalPhoneNumber", "addressComponents"];
  await place.fetchFields({ fields: fieldsToGet });

  return place;
};

/**
 * This converts a google Place object into a formatted Address object, or undefined if failure
 * @param placeService The place returned from google places API. Must have addressComponents field.
 * @returns Address
 */
const getPlaceAsAddress = (placeService: google.maps.places.Place) => {
  const components = placeService.addressComponents;
  if (!components) return undefined;

  const getComponent = (type: string) =>
    components.find((component) => component.types.includes(type))?.longText ||
    "";

  const getComponentShort = (type: string) =>
    components.find((component) => component.types.includes(type))?.shortText ||
    "";

  const city =
    getComponent("locality") ||
    getComponent("postal_town") ||
    getComponent("sublocality") ||
    getComponent("administrative_area_level_3") ||
    getComponent("neighborhood");
  const streetNum = getComponent("street_number");
  const route = getComponent("route");

  if (!route || !streetNum) return undefined;

  const final: Address = {
    id: "",
    first_line: `${streetNum} ${route}`.trim(),
    second_line: "",
    city: city,
    state: getComponentShort("administrative_area_level_1"),
    zip_code: +getComponent("postal_code"),
  } as Address;

  return final;
};

/**
 * Requests autocomplete suggestions for the given string
 * from Google Place API.
 *
 * @param input Text input to filter results
 * @param includedPrimaryTypes optional; sets the includedPrimaryTypes for the request. Default is "geocode"
 * @returns google.maps.places.AutocompleteSuggestion
 */
const getAutocompleteSuggestionFromInput = async (
  input: string,
  includedPrimaryTypes: Array<string> = ["geocode"],
): Promise<Array<google.maps.places.AutocompleteSuggestion>> => {
  if (!input) return [];
  const { AutocompleteSessionToken, AutocompleteSuggestion } =
    (await google.maps.importLibrary("places")) as google.maps.PlacesLibrary;
  const token = new AutocompleteSessionToken();
  const request: google.maps.places.AutocompleteRequest = {
    input: input,
    includedPrimaryTypes: includedPrimaryTypes,
    region: "us",
    language: "en-US",
    sessionToken: token,
  };

  const result = (
    await AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
  ).suggestions;

  return result;
};

export default {
  getSuggestionAsAddress,
  getAutocompleteSuggestionFromInput,
  getSuggestionAsPlace,
  getPlaceAsAddress,
};
